import React, {useCallback, useEffect, useMemo, useState} from "react";
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    CircularProgress,
    IconButton,
    Link,
    Stack,
    Tooltip,
    Typography,
    useTheme
} from "@mui/material";
import {
    CheckCircleOutline as CheckIcon,
    History as HistoryIcon,
    Refresh as RefreshIcon,
    Storefront as VendaIcon
} from "@mui/icons-material";
import {Gauge, LayoutDashboard, Settings2} from "lucide-react";
import {Link as RouterLink, useLocation, useNavigate, useParams} from "react-router-dom";
import {FeedbackSnackbar, PageHeader} from "../../shared/ui";
import {useSistemaPagina} from "./useSistemaPagina";
import {
    alvoDaPendencia,
    gravarMemoria,
    idDaFuncionalidade,
    idDaSecao,
    idDoModulo,
    lerMemoria,
    memoriaVazia,
    novoRecurso,
    recursosParaPayload,
    secaoDoEndereco
} from "./sistemaPagina";
import {getBackendEntityMessage} from "./sistemasUtils";
import PendenciasSecao from "./components/PendenciasSecao";
import DadosSecao from "./components/DadosSecao";
import CatalogoSecao from "./components/CatalogoSecao";
import IntegracaoSecao from "./components/IntegracaoSecao";
import {FiscalSecao} from "./components/SecoesSemApi";
import MetricasSecao from "./components/MetricasSecao";
import NavegacaoSecoes from "./components/NavegacaoSecoes";
import ConfirmacaoDialog, {EfeitoDoMovimento} from "./components/ConfirmacaoDialog";
import PorAVendaDialog from "./components/PorAVendaDialog";
import {FonteChip, SistemaLogo, StatusChip} from "./components/SistemaVisual";

const horaAgora = () => new Date().toLocaleTimeString('pt-BR', {hour: '2-digit', minute: '2-digit'});

const rolarSuave = () => !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

/*
 * Página única do sistema (BBF-174, protótipo v2 validado em 18/10/2026): pendências, dados, árvore
 * módulo → funcionalidade → endpoints, métricas, fiscal e integração, sem abas. Serve para criar e para
 * evoluir: tudo é salvo na hora, inclusive incompleto, e a árvore volta aberta onde estava.
 */
// Cada sistema tem a sua memória do que estava aberto: trocar de sistema monta a página do zero.
export default function SistemaPage(props) {
    const {id} = useParams();
    return <PaginaDoSistema key={id} {...props} />;
}

function PaginaDoSistema({openParent = true}) {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const {id, secao: secaoDaUrl} = useParams();
    const secaoAtual = secaoDoEndereco(secaoDaUrl) || 'pendencias';
    const [open, setOpen] = useState(openParent);
    const pagina = useSistemaPagina(id);
    const {sistema, modulos, pendencias, loading, erro} = pagina;

    const [memoria, setMemoria] = useState(() => lerMemoria(id) || memoriaVazia());
    const [salvo, setSalvo] = useState(null);
    const [feedback, setFeedback] = useState({error: '', success: ''});
    const [dialogo, setDialogo] = useState(null);
    const [venda, setVenda] = useState({aberto: false, carregando: false});
    const [destaque, setDestaque] = useState(null);
    const [rolarPara, setRolarPara] = useState(null);
    const [novoModuloAberto, setNovoModuloAberto] = useState(Boolean(location.state?.novoModulo));
    const [novaFuncionalidadeEm, setNovaFuncionalidadeEm] = useState(null);

    useEffect(() => {
        gravarMemoria(id, memoria);
    }, [id, memoria]);

    // Na primeira visita, o primeiro módulo já vem aberto.
    useEffect(() => {
        if (!memoria.iniciado && modulos.length) {
            setMemoria((atual) => ({...atual, iniciado: true, abertos: {[modulos[0].id]: true, ...atual.abertos}}));
        }
    }, [memoria.iniciado, modulos]);

    // A seção do endereço é rolada assim que a página carrega.
    useEffect(() => {
        if (!loading && sistema && secaoDaUrl) setRolarPara(idDaSecao(secaoAtual));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, Boolean(sistema)]);

    useEffect(() => {
        if (!rolarPara) return undefined;
        const elemento = document.getElementById(rolarPara);
        if (elemento) {
            elemento.scrollIntoView({block: rolarPara.startsWith('sec-') ? 'start' : 'center', behavior: rolarSuave() ? 'smooth' : 'auto'});
        }
        setRolarPara(null);
        return undefined;
    }, [rolarPara]);

    useEffect(() => {
        if (!destaque) return undefined;
        const timer = window.setTimeout(() => setDestaque(null), 1800);
        return () => window.clearTimeout(timer);
    }, [destaque]);

    const marcar = useCallback((texto, onde = {}) => {
        const hora = horaAgora();
        setSalvo({texto, hora});
        setMemoria((atual) => ({...atual, ultimo: {texto, hora, ...onde}}));
    }, []);

    const mostrarErro = useCallback((error) => {
        setFeedback({error: getBackendEntityMessage(error, 'Não foi possível salvar.'), success: ''});
    }, []);

    const abrir = useCallback(({moduloId, funcionalidadeId}) => {
        setMemoria((atual) => ({
            ...atual,
            abertos: moduloId ? {...atual.abertos, [moduloId]: true} : atual.abertos,
            funcs: funcionalidadeId ? {...atual.funcs, [funcionalidadeId]: true} : atual.funcs,
        }));
    }, []);

    const irPara = useCallback(({secao, moduloId, funcionalidadeId}) => {
        abrir({moduloId, funcionalidadeId});
        if (funcionalidadeId) {
            setRolarPara(idDaFuncionalidade(funcionalidadeId));
            setDestaque(funcionalidadeId);
        } else if (moduloId) {
            setRolarPara(idDoModulo(moduloId));
            setDestaque(moduloId);
        } else {
            setRolarPara(idDaSecao(secao));
        }
    }, [abrir]);

    const irParaSecao = useCallback((secao) => {
        navigate(`/sistemas/${id}/${secao}`, {replace: true});
        setRolarPara(idDaSecao(secao));
    }, [id, navigate]);

    const continuar = () => {
        const ultimo = memoria.ultimo;
        if (ultimo) irPara({secao: ultimo.secao || 'dados', moduloId: ultimo.modulo, funcionalidadeId: ultimo.funcionalidade});
    };

    /* ---------- gravações ---------- */

    const salvarSistema = useCallback(async (campos, rotulo) => {
        await pagina.salvarSistema(campos);
        marcar(rotulo, {secao: 'dados'});
    }, [marcar, pagina]);

    const salvarIntegracao = useCallback(async (campos, rotulo) => {
        await pagina.salvarIntegracao(campos);
        marcar(rotulo, {secao: 'integracao'});
    }, [marcar, pagina]);

    const salvarMetricas = useCallback(async (metricas, rotulo, sucesso) => {
        await pagina.salvarMetricas(metricas);
        marcar(rotulo, {secao: 'metricas'});
        setFeedback({error: '', success: sucesso});
    }, [marcar, pagina]);

    const ondeEsta = (funcionalidade) => ({secao: 'catalogo', modulo: funcionalidade.modulo_id, funcionalidade: funcionalidade.id});

    const acoes = useMemo(() => ({
        onErro: mostrarErro,
        alternarModulo: (moduloId) => setMemoria((atual) => ({...atual, abertos: {...atual.abertos, [moduloId]: !atual.abertos[moduloId]}})),
        alternarFuncionalidade: (funcionalidadeId) => setMemoria((atual) => ({...atual, funcs: {...atual.funcs, [funcionalidadeId]: !atual.funcs[funcionalidadeId]}})),
        todos: (abrirTodos) => setMemoria((atual) => ({
            ...atual,
            abertos: Object.fromEntries(modulos.map((modulo) => [modulo.id, abrirTodos])),
            funcs: abrirTodos ? atual.funcs : {},
        })),
        abrirNovoModulo: (aberto) => {
            setNovoModuloAberto(aberto);
            if (!aberto && location.state?.novoModulo) navigate(location.pathname, {replace: true, state: null});
        },
        abrirNovaFuncionalidade: setNovaFuncionalidadeEm,
        criarModulo: async (dados) => {
            const moduloId = await pagina.criarModulo(dados);
            marcar(`módulo ${dados.nome}`, {secao: 'catalogo', modulo: moduloId});
            if (moduloId) {
                abrir({moduloId});
                setNovaFuncionalidadeEm(moduloId);
                setRolarPara(idDoModulo(moduloId));
            }
            setFeedback({error: '', success: `Módulo ${dados.nome} criado. Ele fica com pendência até ter uma funcionalidade completa.`});
        },
        salvarModulo: async (modulo, campos, rotulo) => {
            await pagina.salvarModulo(modulo, campos);
            marcar(rotulo, {secao: 'catalogo', modulo: modulo.id});
        },
        pedirRemoverModulo: (modulo) => setDialogo({tipo: 'remover-modulo', modulo}),
        criarFuncionalidade: async (modulo, dados) => {
            const funcionalidadeId = await pagina.criarFuncionalidade(modulo.id, {...dados, recursos: dados.recursos.map((recurso) => novoRecurso(recurso.metodo, recurso.endpoint))});
            marcar(`funcionalidade ${dados.nome}`, {secao: 'catalogo', modulo: modulo.id, funcionalidade: funcionalidadeId});
            if (funcionalidadeId) {
                abrir({moduloId: modulo.id, funcionalidadeId});
                setRolarPara(idDaFuncionalidade(funcionalidadeId));
            }
            setFeedback({
                error: '',
                success: dados.recursos.length ? `${dados.nome} criada e completa.` : `${dados.nome} criada em construção: falta o endpoint. Já está salva.`,
            });
        },
        salvarFuncionalidade: async (funcionalidade, campos, rotulo) => {
            await pagina.salvarFuncionalidade(funcionalidade, campos);
            marcar(rotulo, ondeEsta(funcionalidade));
        },
        adicionarEndpoint: async (funcionalidade, metodo, caminho) => {
            const recursos = recursosParaPayload([...(funcionalidade.recursos || []), novoRecurso(metodo, caminho)]);
            await pagina.salvarFuncionalidade(funcionalidade, {recursos});
            marcar(`endpoint em ${funcionalidade.nome}`, ondeEsta(funcionalidade));
        },
        pedirRemoverEndpoint: (funcionalidade, recurso) => setDialogo({tipo: 'remover-endpoint', funcionalidade, recurso}),
        pedirMover: async (funcionalidade, destinoId) => {
            const destino = modulos.find((modulo) => modulo.id === destinoId);
            setDialogo({tipo: 'mover', funcionalidade, destino, carregando: true});
            try {
                const efeito = await pagina.efeito(funcionalidade, destinoId);
                setDialogo((atual) => (atual?.funcionalidade === funcionalidade ? {...atual, carregando: false, efeito} : atual));
            } catch (error) {
                setDialogo((atual) => (atual?.funcionalidade === funcionalidade ? {...atual, carregando: false, erroDoEfeito: getBackendEntityMessage(error, 'Não deu para calcular o efeito.')} : atual));
            }
        },
        pedirRemover: async (funcionalidade) => {
            setDialogo({tipo: 'remover-funcionalidade', funcionalidade, carregando: true});
            try {
                const efeito = await pagina.efeito(funcionalidade);
                setDialogo((atual) => (atual?.funcionalidade === funcionalidade ? {...atual, carregando: false, efeito} : atual));
            } catch (error) {
                setDialogo((atual) => (atual?.funcionalidade === funcionalidade ? {...atual, carregando: false, erroDoEfeito: getBackendEntityMessage(error, 'Não deu para calcular o efeito.')} : atual));
            }
        },
    }), [abrir, location.pathname, location.state, marcar, modulos, mostrarErro, navigate, pagina]);

    const confirmarDialogo = async () => {
        const d = dialogo;
        if (d.tipo === 'mover') {
            await pagina.moverFuncionalidade(d.funcionalidade, d.destino.id);
            abrir({moduloId: d.destino.id, funcionalidadeId: d.funcionalidade.id});
            marcar(`${d.funcionalidade.nome} movida para ${d.destino.nome}`, {secao: 'catalogo', modulo: d.destino.id, funcionalidade: d.funcionalidade.id});
            setFeedback({error: '', success: `${d.funcionalidade.nome} movida para ${d.destino.nome}.`});
            setRolarPara(idDaFuncionalidade(d.funcionalidade.id));
        }
        if (d.tipo === 'remover-funcionalidade') {
            await pagina.removerFuncionalidade(d.funcionalidade);
            marcar(`${d.funcionalidade.nome} removida`, {secao: 'catalogo', modulo: d.funcionalidade.modulo_id});
            setFeedback({error: '', success: `${d.funcionalidade.nome} removida do catálogo.`});
        }
        if (d.tipo === 'remover-modulo') {
            await pagina.removerModulo(d.modulo.id);
            marcar(`módulo ${d.modulo.nome} removido`, {secao: 'catalogo'});
            setFeedback({error: '', success: `Módulo ${d.modulo.nome} removido do catálogo.`});
        }
        if (d.tipo === 'remover-endpoint') {
            const recursos = recursosParaPayload((d.funcionalidade.recursos || []).filter((recurso) => recurso.id !== d.recurso.id));
            await pagina.salvarFuncionalidade(d.funcionalidade, {recursos});
            marcar(`endpoint removido de ${d.funcionalidade.nome}`, ondeEsta(d.funcionalidade));
        }
        setDialogo(null);
    };

    /* ---------- pôr à venda ---------- */

    const abrirVenda = async () => {
        setVenda({aberto: true, carregando: true});
        await pagina.recarregarPendencias();
        setVenda({aberto: true, carregando: false});
    };

    const porAVenda = async () => {
        await pagina.porSistemaAVenda();
        setVenda({aberto: false, carregando: false});
        marcar('posto à venda', {secao: 'dados'});
        setFeedback({error: '', success: `${sistema.nome} está à venda. Os planos do sistema já podem ser publicados.`});
    };

    const menus = [
        {label: "Dashboard", id: "dashboard", selected: false, disabled: false, icon: <LayoutDashboard size={20} />, onClick: () => navigate("/")},
        {label: "Sistemas", id: "sistemas", selected: true, disabled: false, icon: <Settings2 size={20} />, onClick: () => navigate("/sistemas")},
        {label: "Métricas", id: "metricas", selected: false, disabled: false, icon: <Gauge size={20} />, onClick: () => navigate("/metricas")},
    ];

    return (
        <Box sx={{display: 'flex', minHeight: '100dvh'}}>
            <PageHeader
                title="Sistemas"
                subtitle=""
                titleAction={() => navigate("/")}
                itemsMenu={menus}
                onHandleMenuOpen={() => null}
                onHandleDrawerToggle={setOpen}
                openParent={open}
            />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: {xs: 2, md: 6},
                    minWidth: 0,
                    boxSizing: 'border-box',
                    mt: 8,
                    backgroundColor: theme.palette.background.default,
                    minHeight: 'calc(100dvh - 64px)',
                }}
            >
                <Stack spacing={3}>
                    <Breadcrumbs>
                        <Link component={RouterLink} to="/sistemas" underline="hover" color="inherit">Sistemas</Link>
                        <Typography color="text.primary">{sistema?.nome || 'Sistema'}</Typography>
                    </Breadcrumbs>

                    {erro ? <Alert severity="error">{erro}</Alert> : null}

                    {loading && !sistema ? (
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{py: 6}}>
                            <CircularProgress size={20} />
                            <Typography color="text.secondary">Carregando o sistema…</Typography>
                        </Stack>
                    ) : null}

                    {sistema ? (
                        <>
                            <Stack direction={{xs: 'column', md: 'row'}} spacing={2} justifyContent="space-between" alignItems={{md: 'flex-start'}}>
                                <Stack direction="row" spacing={2} alignItems="center" sx={{minWidth: 0}}>
                                    <SistemaLogo sistema={sistema} tamanho={48} />
                                    <Box sx={{minWidth: 0}}>
                                        <Typography variant="h4" component="h1" fontWeight={700} sx={{overflowWrap: 'anywhere', fontSize: {xs: '1.6rem', md: '2.125rem'}}}>{sistema.nome}</Typography>
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                            <StatusChip status={sistema.status} />
                                            <FonteChip sistema={sistema} />
                                            <Typography variant="body2" color="text.secondary" sx={{fontFamily: 'monospace'}}>chave {sistema.chave}</Typography>
                                        </Stack>
                                    </Box>
                                </Stack>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Tooltip title="Atualizar">
                                        <span>
                                            <IconButton onClick={pagina.recarregar} disabled={loading} aria-label="Atualizar">
                                                {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    {sistema.status === 'DESENVOLVIMENTO' ? (
                                        <Button variant="contained" startIcon={<VendaIcon />} onClick={abrirVenda}>Pôr à venda</Button>
                                    ) : null}
                                </Stack>
                            </Stack>

                            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Stack direction="row" spacing={0.75} alignItems="center" sx={{color: 'success.main'}}>
                                    <CheckIcon fontSize="small" />
                                    <Typography variant="body2" color="text.secondary">
                                        {salvo ? `Salvo às ${salvo.hora}: ${salvo.texto}` : 'Tudo salvo. Cada mudança vai para o sistema na hora.'}
                                    </Typography>
                                </Stack>
                                {!salvo && memoria.ultimo ? (
                                    <Button size="small" startIcon={<HistoryIcon />} onClick={continuar} sx={{textAlign: 'left', justifyContent: 'flex-start'}}>
                                        Continuar de onde parou: {memoria.ultimo.texto}{memoria.ultimo.hora ? ` (${memoria.ultimo.hora})` : ''}
                                    </Button>
                                ) : null}
                            </Stack>

                            <Box sx={{display: 'grid', gap: 3, gridTemplateColumns: {xs: 'minmax(0, 1fr)', md: '240px minmax(0, 1fr)'}}}>
                                <NavegacaoSecoes sistema={sistema} pendencias={pendencias} secaoAtual={secaoAtual} onIr={irParaSecao} />
                                <Stack spacing={3} sx={{minWidth: 0}}>
                                    <PendenciasSecao sistema={sistema} pendencias={pendencias} modulos={modulos} onIr={(item) => irPara(alvoDaPendencia(item))} />
                                    <DadosSecao sistema={sistema} pendencias={pendencias} onSalvar={salvarSistema} onErro={mostrarErro} />
                                    <CatalogoSecao
                                        sistema={sistema}
                                        modulos={modulos}
                                        pendencias={pendencias}
                                        memoria={memoria}
                                        destaque={destaque}
                                        novoModuloAberto={novoModuloAberto}
                                        novaFuncionalidadeEm={novaFuncionalidadeEm}
                                        acoes={acoes}
                                    />
                                    <MetricasSecao
                                        sistema={sistema}
                                        pendencias={pendencias}
                                        catalogo={pagina.metricas}
                                        erroDoCatalogo={pagina.erroDasMetricas}
                                        onSalvar={salvarMetricas}
                                        onRecusa={(texto) => setFeedback({error: texto, success: ''})}
                                    />
                                    <FiscalSecao sistema={sistema} pendencias={pendencias} />
                                    <IntegracaoSecao sistema={sistema} pendencias={pendencias} onSalvar={salvarIntegracao} onErro={mostrarErro} />
                                </Stack>
                            </Box>
                        </>
                    ) : null}
                </Stack>

                <FeedbackSnackbar feedback={feedback} onClose={() => setFeedback({error: '', success: ''})} />

                <DialogoDoCatalogo dialogo={dialogo} onConfirmar={confirmarDialogo} onFechar={() => setDialogo(null)} />

                <PorAVendaDialog
                    aberto={venda.aberto}
                    carregando={venda.carregando}
                    sistema={sistema}
                    pendencias={pendencias}
                    onPorAVenda={porAVenda}
                    onVerPendencias={() => {
                        setVenda({aberto: false, carregando: false});
                        irParaSecao('pendencias');
                    }}
                    onFechar={() => setVenda({aberto: false, carregando: false})}
                />
            </Box>
        </Box>
    );
}

// Mover, remover funcionalidade, remover módulo e remover endpoint: a confirmação diz antes o que muda.
const DialogoDoCatalogo = ({dialogo, onConfirmar, onFechar}) => {
    const d = dialogo || {};
    const f = d.funcionalidade;
    const comFuncionalidades = d.modulo?.funcionalidades?.length || 0;

    const conteudo = {
        mover: {
            titulo: `Mover ${f?.nome} para ${d.destino?.nome}?`,
            botao: 'Mover',
            corpo: (
                <>
                    <Typography variant="body2">
                        A funcionalidade passa a valer onde o módulo {d.destino?.nome} vale, com os endpoints e as concessões dos perfis. O efeito é imediato.
                    </Typography>
                    <EfeitoDoMovimento efeito={d.efeito} />
                </>
            ),
        },
        'remover-funcionalidade': {
            titulo: `Remover ${f?.nome}?`,
            botao: 'Remover',
            perigo: true,
            corpo: (
                <>
                    <Typography variant="body2">
                        A funcionalidade sai do catálogo, e a chave <Box component="span" sx={{fontFamily: 'monospace'}}>{f?.chave}</Box> deixa de existir. Os perfis que concedem os endpoints dela perdem a concessão.
                    </Typography>
                    <EfeitoDoMovimento efeito={d.efeito} />
                </>
            ),
        },
        'remover-modulo': {
            titulo: `Remover o módulo ${d.modulo?.nome}?`,
            botao: 'Remover',
            perigo: true,
            bloqueado: comFuncionalidades > 0,
            corpo: comFuncionalidades ? (
                <Alert severity="warning" variant="outlined">
                    O módulo tem {comFuncionalidades === 1 ? '1 funcionalidade' : `${comFuncionalidades} funcionalidades`}. Mova ou remova cada uma antes: o catálogo não apaga em cascata.
                </Alert>
            ) : (
                <Typography variant="body2">
                    O módulo sai do catálogo. Se ele estiver em algum plano, a remoção é recusada: tire-o do plano antes.
                </Typography>
            ),
        },
        'remover-endpoint': {
            titulo: `Remover ${d.recurso?.metodo} ${d.recurso?.endpoint}?`,
            botao: 'Remover',
            perigo: true,
            corpo: (
                <Typography variant="body2">
                    {(f?.recursos?.length || 0) === 1
                        ? `${f?.nome} fica sem endpoint e sai do direito de todas as assinaturas até ganhar outro. Fica salva em construção.`
                        : `O endpoint sai de ${f?.nome}, e os perfis que o concediam perdem a concessão.`}
                </Typography>
            ),
        },
    }[d.tipo];

    return (
        <ConfirmacaoDialog
            aberto={Boolean(dialogo)}
            titulo={conteudo?.titulo}
            botao={conteudo?.botao}
            perigo={conteudo?.perigo}
            bloqueado={conteudo?.bloqueado}
            carregando={Boolean(d.carregando)}
            onConfirmar={onConfirmar}
            onFechar={onFechar}
        >
            {conteudo?.corpo}
            {d.erroDoEfeito ? <Alert severity="warning">Não deu para calcular quem ganha ou perde acesso: {d.erroDoEfeito}</Alert> : null}
        </ConfirmacaoDialog>
    );
};
